import { z } from 'zod';
import { KAMPAGNE_TYP_LISTE, type KampagneTyp } from './kampagnen';

/**
 * Retail Media RM-7: Berichte je Werbekonto (Händlerportal) und je Marktplatz (Konto).
 *
 * Gelesen wird ausschließlich der Tages-Rollup kennzahl_tag (nie die Rohdaten). Gespeichert sind nur Zähler in
 * Ganzzahlen bzw. Cent; Quoten (CTR, CPC, ROAS, Conversion-Rate) werden bei jeder Abfrage aus den Summen berechnet.
 * Leitplanken: Auswertung des Auktionsprotokolls der Buy Box – wie oft und warum ein Gebot aus der Wertung fiel.
 *
 * RM-9: Gruppierung und Filter nach Format (Kampagnentyp) und nach Kanal (nur Offsite, Tagessummen
 * offsite_kanal_tag). „Neukunden der Marke“ kommen aus der Tagessumme neukunde_tag (Definition in Migration 0540):
 * zugeordnete Bestellung, deren Werbesitzung in den 12 Monaten davor keine zugeordnete Bestellung desselben
 * Werbekontos hatte.
 */

export const BERICHT_GRENZEN = {
  /** höchstens so viele Tage je Bericht */
  maxTage: 366,
  /** Leitplanken lesen das Auktionsprotokoll – höchstens so viele Tage je Abfrage */
  leitplankenMaxTage: 31,
  /** so viele Zeilen je Gruppierung in der Antwort (der Export enthält alle) */
  zeilenMax: 500,
} as const;

export const BERICHT_GRUPPIERUNGEN = {
  tag: 'Tag',
  kampagne: 'Kampagne',
  platzierung: 'Platzierung',
  werbekonto: 'Werbekonto',
  format: 'Format',
  kanal: 'Kanal',
} as const;
export type BerichtGruppierung = keyof typeof BERICHT_GRUPPIERUNGEN;
export const BERICHT_GRUPPIERUNG_LISTE = ['tag', 'kampagne', 'platzierung', 'werbekonto', 'format', 'kanal'] as const satisfies readonly BerichtGruppierung[];
/** Im Händlerportal gibt es nur das eigene Werbekonto */
export const PORTAL_GRUPPIERUNGEN = ['kampagne', 'format', 'kanal', 'platzierung', 'tag'] as const satisfies readonly BerichtGruppierung[];

/** Kennzahlen, auf die es je Format ankommt (Vorgabe docs/retail-media-formate.md) – die Oberfläche hebt sie hervor */
export type BerichtKpi = 'roas' | 'konversion' | 'ctr' | 'neukunden' | 'impressionen' | 'klicks';
export const BERICHT_KPI_NAMEN: Record<BerichtKpi, string> = {
  roas: 'ROAS',
  konversion: 'Conversion Rate',
  ctr: 'Klickrate',
  neukunden: 'Neukunden der Marke',
  impressionen: 'Impressionen',
  klicks: 'Klicks',
};
export const FORMAT_KPIS: Record<KampagneTyp, readonly BerichtKpi[]> = {
  sponsored_product: ['roas', 'konversion'],
  sponsored_brand: ['ctr', 'neukunden'],
  display: ['impressionen', 'ctr'],
  offsite: ['klicks', 'roas'],
};

/** Leitplanken der Buy Box (RM-5) mit Erklärung für Berichte – Schlüssel wie im Auktionsprotokoll */
export const LEITPLANKEN = {
  preisfenster: 'Preis außerhalb des Preisfensters',
  bestand: 'kein Bestand',
  bewertung: 'Bewertung unter dem Schwellwert',
  lieferzeit: 'Lieferzeit zu lang',
  rotation: 'Rotationsdeckel erreicht',
  budget: 'Budget oder Guthaben reicht nicht',
} as const;
export type Leitplanke = keyof typeof LEITPLANKEN;

export const LEITPLANKEN_TIPP: Record<Leitplanke, string> = {
  preisfenster: 'Ihr Preis lag zu weit über dem günstigsten Angebot. Ein niedrigerer Preis bringt das Gebot zurück in die Wertung.',
  bestand: 'Für das Angebot war kein Bestand gemeldet. Pflegen Sie den Bestand im Händlerportal oder über die Anbindung.',
  bewertung: 'Die Bewertung lag unter dem Schwellwert des Marktplatzes.',
  lieferzeit: 'Die Lieferzeit war deutlich länger als die des schnellsten Angebots. Eine kürzere Lieferzeit hilft.',
  rotation: 'Das Angebot hatte an diesem Tag bereits die meisten Buy Boxen des Artikels bezahlt gewonnen – andere kommen auch zum Zug.',
  budget: 'Kampagnenbudget, Tagesbudget oder Guthaben reichten für das Gebot nicht. Laden Sie Guthaben auf oder erhöhen Sie das Budget.',
};

const DATUM = /^\d{4}-\d{2}-\d{2}$/;
const gueltigesDatum = (s: string) => {
  if (!DATUM.test(s)) return false;
  const d = new Date(`${s}T00:00:00Z`);
  return !Number.isNaN(d.getTime()) && d.toISOString().slice(0, 10) === s;
};
const Datum = z.string().refine(gueltigesDatum, 'Bitte ein Datum im Format JJJJ-MM-TT angeben');

/** Tage von bis einschließlich bis */
export function tageZwischen(von: string, bis: string): number {
  return Math.round((Date.parse(`${bis}T00:00:00Z`) - Date.parse(`${von}T00:00:00Z`)) / 86_400_000) + 1;
}

const zeitraumPruefen = (max: number) => (a: { von: string; bis: string }, ctx: z.RefinementCtx) => {
  if (!gueltigesDatum(a.von) || !gueltigesDatum(a.bis)) return;
  const tage = tageZwischen(a.von, a.bis);
  if (tage < 1) ctx.addIssue({ code: 'custom', path: ['bis'], message: 'Das Ende liegt vor dem Beginn' });
  else if (tage > max) ctx.addIssue({ code: 'custom', path: ['bis'], message: `Höchstens ${max} Tage je Abfrage` });
};

const Filter = {
  von: Datum,
  bis: Datum,
  werbekonto: z.string().uuid().optional(),
  kampagne: z.string().uuid().optional(),
};
/** RM-9: nur Kampagnen dieses Formats */
const FormatFilter = z.enum(KAMPAGNE_TYP_LISTE).optional();

export const BerichtAbfrage = z.object({
  ...Filter,
  format: FormatFilter,
  gruppierung: z.enum(BERICHT_GRUPPIERUNG_LISTE).default('tag'),
}).superRefine(zeitraumPruefen(BERICHT_GRENZEN.maxTage));
export type BerichtAbfrageEingabe = z.infer<typeof BerichtAbfrage>;

/** Händlerportal: fest auf das eigene Werbekonto – kein Filter und keine Gruppierung nach Werbekonto */
export const PortalBerichtAbfrage = z.object({
  von: Filter.von,
  bis: Filter.bis,
  kampagne: Filter.kampagne,
  format: FormatFilter,
  gruppierung: z.enum(PORTAL_GRUPPIERUNGEN).default('tag'),
}).superRefine(zeitraumPruefen(BERICHT_GRENZEN.maxTage));

export const LeitplankenAbfrage = z.object(Filter).superRefine(zeitraumPruefen(BERICHT_GRENZEN.leitplankenMaxTage));
export const PortalLeitplankenAbfrage = z.object({ von: Filter.von, bis: Filter.bis, kampagne: Filter.kampagne })
  .superRefine(zeitraumPruefen(BERICHT_GRENZEN.leitplankenMaxTage));

/* ------------------------------------------------------------------ Antworten */

/** Zähler aus dem Rollup – Ganzzahlen, Beträge in Cent */
export interface BerichtSummen {
  impressionen: number;
  klicks: number;
  kostenCent: number;
  bestellungen: number;
  umsatzCent: number;
  /** Neukunden der Marke (zugeordnete Bestellungen ohne frühere desselben Werbekontos in 12 Monaten) */
  neukunden: number;
}

/** Zähler plus berechnete Quoten (null, wenn der Nenner 0 ist) */
export interface BerichtKennzahlen extends BerichtSummen {
  /** Klicks / Impressionen, 0..1 */
  ctr: number | null;
  /** Kosten / Klicks in Cent */
  cpcCent: number | null;
  /** Umsatz / Kosten */
  roas: number | null;
  /** Bestellungen / Klicks, 0..1 */
  konversion: number | null;
  /** Neukunden / Bestellungen, 0..1 */
  neukundenAnteil: number | null;
}

export interface BerichtZeile extends BerichtKennzahlen {
  /** Tag (JJJJ-MM-TT), Kampagnen-/Werbekonto-ID oder Platzierungsschlüssel */
  schluessel: string;
  titel: string;
  /** z. B. Werbekonto einer Kampagne */
  zusatz: string | null;
  /** Format der Kampagne (Gruppierung nach Kampagne bzw. Format) */
  format?: KampagneTyp;
}

export interface BerichtTag extends BerichtSummen {
  tag: string;
}

export interface WerbeBericht {
  von: string;
  bis: string;
  gruppierung: BerichtGruppierung;
  summen: BerichtKennzahlen;
  zeilen: BerichtZeile[];
  /** Anzahl aller Gruppen – zeilen enthält höchstens BERICHT_GRENZEN.zeilenMax */
  zeilenGesamt: number;
  /** jeder Tag des Zeitraums, auch ohne Daten */
  reihe: BerichtTag[];
  /** letzter Rechenlauf des Rollups im Zeitraum */
  berechnetAm: string | null;
  /** Namen der gesetzten Filter (null: kein Filter oder nicht gefunden) */
  filter: { werbekonto: BerichtFilterName | null; kampagne: BerichtFilterName | null; format: KampagneTyp | null };
}

export interface BerichtFilterName {
  id: string;
  name: string;
}

export interface LeitplankenBericht {
  von: string;
  bis: string;
  /** Buy-Box-Auktionen mit mindestens einem Gebot (des Werbekontos bzw. Filters) */
  auktionen: number;
  /** davon mindestens ein Gebot aus der Wertung gefallen */
  ausgeschlossen: number;
  /** davon bezahlt gewonnen */
  gewonnen: number;
  /** je Leitplanke, häufigste zuerst – ein Gebot kann mehrere verletzen */
  leitplanken: { leitplanke: Leitplanke; anzahl: number }[];
}

export function kennzahlenAus(s: BerichtSummen): BerichtKennzahlen {
  return {
    ...s,
    ctr: s.impressionen ? s.klicks / s.impressionen : null,
    cpcCent: s.klicks ? s.kostenCent / s.klicks : null,
    roas: s.kostenCent ? s.umsatzCent / s.kostenCent : null,
    konversion: s.klicks ? s.bestellungen / s.klicks : null,
    neukundenAnteil: s.bestellungen ? Math.min(1, s.neukunden / s.bestellungen) : null,
  };
}

export const leereSummen = (): BerichtSummen => ({ impressionen: 0, klicks: 0, kostenCent: 0, bestellungen: 0, umsatzCent: 0, neukunden: 0 });
